import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import { useCallback, useEffect, useState } from "react";

import { useSelector } from "react-redux";
import { fetchPointsTableConfiguration, fetchPointsTableContent, setPointsTableConfiguration, setPointsTableContent } from "../../../api/pointsTable";
import { fetchMatchCenterSources } from "../../../api/matchCenter";
import PrivateComponent from "../../../providers/PrivateComponent/PrivateComponent";
import { PERMISSIONS } from "../../../utils/constants";
import ConfigureList from "../../configurationApi/configuration/confiugreList";
import CompetitionTable from "../competitionTable";
import MatchTree from "../../configurationApi/configurationTree/matchTree";

export default function PointsTableItem({ sport }) {
  const [dialog, setDialog] = useState(false);
  const [configuration, setConfiguration] = useState(null);
  const [initialHash, setInitialHash] = useState(null);
  const [version, setVersion] = useState(null);
  const [sources, setSources] = useState(null);

  const projectId = useSelector((state) => state.project.currentProjectId);

  const createHash = useCallback((configuration, level, parentId) => {
    const hash = configuration.reduce((a, c) => {
      let childrenHash = null;
      if (c.children.length > 0) {
        childrenHash = createHash(c.children, level + 1, c.entityId);
      }
      a[`${level}-${c.entityId}`] = {
        level,
        id: c.entityId,
        parentId,
        displayOrder: c.displayOrder,
        priority: c.priority,
      };
      return { ...a, ...childrenHash };
    }, {});
    return hash;
  }, []);

  const fetchConfiguration = useCallback(async () => {
    if (!projectId) return;
    const configuration = await fetchPointsTableConfiguration(
      projectId,
      sport.id,
    );
    const hashTable = createHash(configuration, 1, null);
    setInitialHash(hashTable);
    setConfiguration(configuration);
  }, [projectId, sport, createHash]);

  const handleGetContentKeys = useCallback(({ level }) => {
    switch (level) {
      case 1:
        return [
          { title: "Points table" },
          { title: "Leagues", contentPostfix: "Leagues" },
          { title: "Competitions", contentPostfix: "Competitions" },
        ];
      case 2:
        return [{ title: "League" }];
      case 3:
        return [{ title: "Competition" }];
      default:
        return null;
    }
  }, []);

  const handleGetContent = useCallback(async (contentKey) => {
    const data = await fetchPointsTableContent(projectId, sport.id, contentKey);
    return data;
  }, [projectId, sport]);

  const handleSetContent = useCallback(async (contentKey, content) => {
    const data = await setPointsTableContent(projectId, sport.id, contentKey, content);
    return data;
  }, [projectId, sport]);

  const fetchSources = useCallback(async () => {
    if (!projectId) return;
    const sources = await fetchMatchCenterSources(projectId, sport.id);
    setSources(sources);
  }, [projectId, sport]);

  const onSubmit = useCallback(() => {
    fetchConfiguration();
    setVersion(Date.now());
  }, [fetchConfiguration]);

  const openDialog = useCallback(() => {
    setDialog(true);
  }, []);

  const closeDialog = useCallback(() => {
    setDialog(false);
  }, []);

  useEffect(() => {
    fetchConfiguration();
    fetchSources();
  }, [fetchConfiguration, fetchSources]);

  return (
    <div style={{ padding: "25px" }}>
      <div style={{ textAlign: "center" }}>
        <h1>Points table structure</h1>
        <PrivateComponent permission={PERMISSIONS.ALLOW_WRITE_STATISTICS}>
          <Button variant="contained" color="primary" onClick={openDialog}>
            Configure
          </Button>
        </PrivateComponent>
        <div>
          <Dialog fullScreen open={dialog} onClose={closeDialog}>
            <AppBar sx={{ position: "relative" }} color="transparent">
              <Toolbar>
                <Button onClick={closeDialog} color="primary">
                  Close
                </Button>
              </Toolbar>
            </AppBar>
            {sources && <ConfigureList
              projectId={projectId}
              sport={sport}
              onCloseDialog={closeDialog}
              onSubmit={onSubmit}
              initialHash={initialHash}
              configuration={configuration}
              setConfiguration={setPointsTableConfiguration}
              sources={sources}
            />}
          </Dialog>
          <MatchTree
            projectId={projectId}
            sport={sport}
            configuration={configuration}
            getContent={handleGetContent}
            getContentKeys={handleGetContentKeys}
            setContent={handleSetContent} />
        </div>
      </div>
      <div style={{ textAlign: "center" }}>
        <h1>Matches</h1>
        <CompetitionTable projectId={projectId} sport={sport} version={version} />
      </div>
    </div>
  );
}
